// File: src/components/general/IconButton.tsx

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "../Button";

type AutoDisableMode = "click" | "timer";

interface IconButtonProps {
  icon: React.ReactNode;
  ariaLabel: string;
  onClick?: (e?:any) => void;
  variant?: "primary" | "secondary" | "ghost" | "outline" | "flat" | "text";
  size?: "small" | "compact" | "fit" | "medium" | "large";
  disabled?: boolean;
  isLoading?: boolean;
  autoDisable?: boolean;
  autoDisableMode?: AutoDisableMode;
  autoDisableTimeout?: number; // in ms
  className?: string;
  rounded?: boolean;
  onMouseDown?: (e?:any) => void;
  onTouchStart?: (e?:any) => void;
}

/** Styled IconButton wrapper — inherits full Button behavior */
const StyledIconButton = styled(Button)`
  svg {
    display: block;
  }
`;

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  ariaLabel,
  onClick,
  variant = "ghost",
  size = "fit",
  disabled = false,
  isLoading = false,
  autoDisable = false,
  autoDisableMode = "click",

  autoDisableTimeout = 2000, // default to 2s

  className,
  rounded = false,
}) => {
  const [clicked, setClicked] = useState(false);
  const [temporarilyDisabled, setTemporarilyDisabled] = useState(false);
  useEffect(() => {
    if (autoDisableMode === "timer" && temporarilyDisabled) {
      const timeout = setTimeout(() => {
        setTemporarilyDisabled(false);
      }, autoDisableTimeout);
      return () => clearTimeout(timeout);
    }
  }, [temporarilyDisabled, autoDisableMode, autoDisableTimeout]);

  const handleClick = () => {
    if (disabled || isLoading || (autoDisable && temporarilyDisabled)) return;

    onClick?.();

    if (autoDisable) {
      if (autoDisableMode === "click") {
        setTemporarilyDisabled(true);
      }
      if (autoDisableMode === "timer") {
        setTemporarilyDisabled(true);
      }
    }
  };

  const isDisabled = disabled || (autoDisable && temporarilyDisabled);

  return (
    <StyledIconButton
      icon={icon}
      ariaLabel={ariaLabel}
      iconOnly
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={isDisabled}
      isLoading={isLoading}
      className={className}
      rounded={rounded} // ✅ now passed properly
    />
  );
};